import { RatingFilled } from '@teko/ds-icon';
import {
  Body,
  Caption,
  Col,
  CustomLink,
  Flex,
  Row,
  ThemeProps,
  Title,
  styled,
} from '@teko/ds-mobile';
import { WishlistButton } from 'components/WishlistButton';
import {
  useFeatureFlags,
  useProductDetailState,
  useTenantConfig,
} from 'contexts';
import { scrollSmoothToIdWithOffset } from 'helpers/page-actions';
import { t } from 'i18n';
import React from 'react';
import { ConvertedProduct } from 'types';

interface IProductInfo {
  product: ConvertedProduct;
}

const shortenString = (name: string, maxLength = 20) => {
  const trimmedName = name?.trim();
  if (!trimmedName) return '';
  if (trimmedName.length <= maxLength) return trimmedName;
  return `${trimmedName.slice(0, maxLength)?.trimEnd()}...`;
};

const ProductBrand = ({
  brandCode,
  brandName,
}: {
  brandCode: string;
  brandName: string;
}) => {
  return (
    <BrandNameTextLink
      href={{
        pathname: '/brands/[brandId]',
        query: {
          brandId: brandCode,
        },
      }}
      as={{
        pathname: `/${brandCode}-brand.${brandCode}`,
      }}
      target="_top"
    >
      {shortenString(brandName)}
    </BrandNameTextLink>
  );
};

export const ProductInfo: React.FC<IProductInfo> = (props) => {
  const { product } = props;
  const { epicOmni1550, isMobile } = useTenantConfig();
  const { flags } = useFeatureFlags();
  const { productRatingData } = useProductDetailState();
  const { totalReviews, averageRating } = productRatingData;

  return (
    <>
      <Row align="top">
        <Col flex={isMobile ? '75%' : '100%'}>
          <Title level={isMobile ? 2 : 1} color="black">
            {product.displayName}
          </Title>
        </Col>
        {isMobile && (
          <Col flex="25%">
            <Flex align="center" justify="end" gap="s4">
              <span
                style={{ fontSize: 13, lineHeight: '20px', fontWeight: 400 }}
              >
                {t('Yêu thích')}
              </span>
              <WishlistButton sku={product.sku} size="24" />
            </Flex>
          </Col>
        )}
      </Row>
      <ProductInfoWrapper isMobile={isMobile}>
        <StyledCaption level={1} marginTop="s4">
          <Text>{t('Thương hiệu:')}</Text>
          {flags.epic_omni_1552 && product?.brands?.length > 0 ? (
            product.brands.map((brand, index) => (
              <React.Fragment key={index}>
                <ProductBrand
                  key={`${brand.code}${brand.name}${index}`}
                  brandCode={brand.code}
                  brandName={brand.name}
                />
                {index + 1 !== product?.brands?.length && ',\xa0'}
              </React.Fragment>
            ))
          ) : (
            <ProductBrand
              brandCode={product?.brand?.code}
              brandName={product?.brand?.name}
            />
          )}
        </StyledCaption>
        {!isMobile ? <Divider /> : null}
        <Row>
          <StyledCaption level={1} marginTop="s4">
            <Text>SKU: {product.sellerSku || product.sku}</Text>
          </StyledCaption>
          {epicOmni1550 && !!totalReviews && (
            <Row align="middle">
              <Divider />
              <RatingFilled size={18} color="support" />
              <Body weight="bold" margin={['s0', 's4']}>
                {averageRating}
              </Body>
              <Body
                weight="regular"
                color="#0200bc"
                onClick={() => scrollSmoothToIdWithOffset('review-section', 60)}
              >{`(${totalReviews} ${t('lượt đánh giá')})`}</Body>
            </Row>
          )}
        </Row>
      </ProductInfoWrapper>
    </>
  );
};

const BrandNameTextLink = styled(CustomLink)(
  ({ theme }: ThemeProps) => `
  color: ${theme?.colors?.primary};
  font-weight: normal;
  margin-left: 4px;
  font-size: 12px;
  line-height: 16px;
  text-decoration: none;
`,
);

const StyledCaption = styled(Caption)`
  display: block;
  & > a,
  span {
    font-size: 13px;
    line-height: 20px;
    font-weight: 400;
  }
`;

const Text = styled.span`
  color: #000000a6;
`;

const ProductInfoWrapper = styled.div<{ isMobile: boolean }>`
  display: flex;
  align-items: ${({ isMobile }) => (isMobile ? 'flex-start' : 'center')};
  flex-direction: ${({ isMobile }) => (isMobile ? 'column' : 'row')};
`;

const Divider = styled.div`
  ::before {
    content: '|';
    margin: 0 24px;
    color: #0000000f;
  }
`;
